import { useMemo } from 'react'
import { HomeViewParticipantFragment } from 'src/participantsHome/searchParticipants.generated'
import { joinName } from '../format'
import {
  AdvancedCaseloadParticipantFragment,
  useGetAdvancedCaseloadParticipantsQuery,
} from './advancedCaseload.generated'
import { ADVANCED_PROGRAM_TAG_NAME } from './advancedProgramTag'
import { useCaseloadIndicatorSignals } from './useCaseloadIndicatorSignals'

export type CaseloadParticipant = HomeViewParticipantFragment & {
  displayName: string
  latestReturnedLabOrderID: string | null
}

// `newMessages` leaves out the role chats this provider holds; the shared chip should count them too.
const withRoleChatMessages = ({
  newMessages,
  advancedRoleChatNewMessages,
}: NonNullable<AdvancedCaseloadParticipantFragment['indicators']>) =>
  newMessages || advancedRoleChatNewMessages
    ? { ...newMessages, count: (newMessages?.count ?? 0) + (advancedRoleChatNewMessages?.count ?? 0) }
    : null

// One `server` refusal nulls the whole `patients` reply; missing participants are ordinary (ADR-005).
export const useAdvancedCaseloadParticipants = (uids: string[]): Record<string, CaseloadParticipant> => {
  const { data } = useGetAdvancedCaseloadParticipantsQuery({ variables: { uids }, skip: uids.length === 0 })
  useCaseloadIndicatorSignals(uids)

  return useMemo(() => {
    const entries = (data?.patients ?? []).map((patient): [string, CaseloadParticipant] => [
      patient.uid,
      {
        ...(patient as Omit<HomeViewParticipantFragment, 'enrollment'> as HomeViewParticipantFragment),
        indicators: patient.indicators && {
          ...patient.indicators,
          newMessages: withRoleChatMessages(patient.indicators),
        },
        tags: patient.tags?.filter((tag) => tag.name !== ADVANCED_PROGRAM_TAG_NAME),
        displayName: patient.preferredName || joinName(patient.firstname, patient.lastname),
        latestReturnedLabOrderID: patient.indicators?.latestReturnedLabOrderID ?? null,
      },
    ])

    return Object.fromEntries(entries)
  }, [data])
}
